import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";

interface SearchResult {
  id: string;
  type: "product" | "provider" | "category";
  title: string;
  subtitle?: string;
  image?: string;
  href: string;
}

interface Props {
  onClose: () => void;
}

export default function GlobalSearch({ onClose }: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80);
  }, []);

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.length < 1) { setResults([]); return; }
    setLoading(true);
    const [
      { data: products },
      { data: providers },
      { data: categories },
    ] = await Promise.all([
      supabase
        .from("products")
        .select("id, title, type, thumbnail_url, provider_id")
        .ilike("title", `%${q}%`)
        .eq("visibility", "public")
        .limit(5),
      supabase
        .from("profiles")
        .select("id, name, service_name, avatar_url, category")
        .or(`name.ilike.%${q}%,service_name.ilike.%${q}%`)
        .limit(5),
      supabase
        .from("categories")
        .select("id, name, description")
        .ilike("name", `%${q}%`)
        .eq("visible", true)
        .limit(3),
    ]);

    const merged: SearchResult[] = [
      ...(products || []).map((p) => ({
        id: p.id,
        type: "product" as const,
        title: p.title,
        subtitle: p.type === "video" ? "動画講座" : "教材キット",
        image: p.thumbnail_url || undefined,
        href: `/products/${p.type}/${p.id}`,
      })),
      ...(providers || []).map((p) => ({
        id: p.id,
        type: "provider" as const,
        title: p.service_name || p.name,
        subtitle: p.category || "事業者",
        image: p.avatar_url || undefined,
        href: `/providers/${p.id}`,
      })),
      ...(categories || []).map((c) => ({
        id: c.id,
        type: "category" as const,
        title: c.name,
        subtitle: c.description || "カテゴリ",
        href: `/categories/${c.id}`,
      })),
    ];
    setResults(merged);
    setSelectedIdx(-1);
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 220);
    return () => clearTimeout(t);
  }, [query, search]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx((i) => Math.min(i + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx((i) => Math.max(i - 1, -1)); }
    if (e.key === "Enter" && selectedIdx >= 0 && results[selectedIdx]) {
      navigate(results[selectedIdx].href);
      onClose();
    }
  };

  const typeIcon: Record<string, string> = {
    product: "ri-play-circle-line",
    provider: "ri-store-2-line",
    category: "ri-folder-3-line",
  };
  const typeColor: Record<string, string> = {
    product: "text-teal",
    provider: "text-gold",
    category: "text-deep-green",
  };
  const typeLabel: Record<string, string> = {
    product: "講座",
    provider: "事業者",
    category: "カテゴリ",
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-stone-100">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-search-line text-xl text-charcoal/40" />
          </div>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="講座名・事業者名・カテゴリを検索..."
            className="flex-1 text-base font-sans text-charcoal placeholder-charcoal/30 outline-none bg-transparent"
          />
          {loading && <i className="ri-loader-4-line animate-spin text-teal text-lg" />}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/40 hover:text-charcoal cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.length === 0 ? (
            <div className="px-5 py-6">
              <p className="text-xs text-charcoal/40 font-sans mb-3">検索のヒント</p>
              <div className="flex flex-wrap gap-2">
                {["和菓子", "書道", "大工", "美容", "陶芸", "料理"].map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setQuery(tag)}
                    className="px-3 py-1.5 rounded-full bg-stone-100 text-sm text-charcoal/60 font-sans cursor-pointer hover:bg-stone-200 transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="px-5 py-10 text-center">
              <i className="ri-search-line text-3xl text-charcoal/20 block mb-2" />
              <p className="text-sm text-charcoal/40 font-sans">「{query}」の検索結果が見つかりませんでした</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-50">
              {results.map((r, idx) => (
                <button
                  key={r.id}
                  onClick={() => { navigate(r.href); onClose(); }}
                  className={`w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors cursor-pointer ${selectedIdx === idx ? "bg-stone-100" : "hover:bg-stone-50"}`}
                >
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-stone-100 flex items-center justify-center">
                    {r.image ? (
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover object-top" />
                    ) : (
                      <i className={`${typeIcon[r.type]} text-xl ${typeColor[r.type]}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-sans text-charcoal font-medium truncate">{r.title}</p>
                    <p className="text-xs text-charcoal/40 font-sans truncate">{r.subtitle}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-sans flex-shrink-0 ${typeColor[r.type]} bg-current/10`}>
                    {typeLabel[r.type]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {results.length > 0 && (
          <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between">
            <p className="text-xs text-charcoal/30 font-sans">{results.length}件見つかりました</p>
            <p className="text-xs text-charcoal/30 font-sans">↑↓ で選択　Enter で開く　Esc で閉じる</p>
          </div>
        )}
      </div>
    </div>
  );
}
